import React, { useState } from "react";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { resetPassword } from "../features/user/userSlice";
import { BsEye, BsEyeSlash, BsLock, BsCheckCircle } from "react-icons/bs";

let passwordSchema = yup.object({
  password: yup
    .string()
    .required("Password is Required")
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password cannot exceed 128 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number and one special character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm Password is Required")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
});

const Resetpassword = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      setErrorMessage("");
      
      dispatch(resetPassword({ token, password: values.password }))
        .then((result) => {
          setIsLoading(false);
          if (result.payload) {
            setShowSuccess(true);
            // Redirect to login after 3 seconds
            setTimeout(() => {
              navigate("/login");
            }, 3000);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          setErrorMessage("Password reset failed. Please try again.");
        });
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <Meta 
        title="Reset Password - VEDA Merchandise"
        description="Set a new password for your VEDA account to access mythological toys, games, and puzzles."
        keywords="reset password, new password, VEDA account, mythological toys login"
        author="VEDA Merchandise"
        ogTitle="Reset Password - VEDA Merchandise"
        ogDescription="Set a new password for your VEDA account to access mythological toys, games, and puzzles."
        ogImage="https://vedamerchandise.com/auth-preview.jpg"
        ogUrl={`https://vedamerchandise.com/reset-password/${token}`}
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="Reset Password - VEDA Merchandise"
        twitterDescription="Set a new password for your VEDA account to access mythological toys, games, and puzzles."
        twitterImage="https://vedamerchandise.com/auth-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl={`https://vedamerchandise.com/reset-password/${token}`}
      />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-4 shadow-lg">
              <BsLock className="text-white text-3xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
            <p className="text-gray-600">Enter your new password below</p>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <BsCheckCircle className="text-green-500 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Password Reset Successfully!</h3>
              <p className="text-gray-600">
                Your password has been updated. You will be redirected to the login page shortly.
              </p>
              <p className="text-gray-600 mt-2">
                Redirecting to login page...
              </p>
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            {/* Error Alert */}
            {(errorMessage || (formik.touched.password && formik.errors.password) || (formik.touched.confirmPassword && formik.errors.confirmPassword)) && (
              <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {errorMessage || formik.errors.password || formik.errors.confirmPassword}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!showSuccess && (
              <form
                onSubmit={formik.handleSubmit}
                className="space-y-6"
              >
                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsLock className="text-red-500 text-lg" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Enter your new password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`block w-full pl-12 pr-12 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        formik.touched.password && formik.errors.password
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showPassword ? <BsEyeSlash className="text-xl" /> : <BsEye className="text-xl" />}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    Password must contain at least 8 characters, including uppercase, lowercase, number and special character.
                  </p>
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsLock className="text-red-500 text-lg" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      id="confirmPassword"
                      placeholder="Confirm your new password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`block w-full pl-12 pr-12 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {showConfirmPassword ? <BsEyeSlash className="text-xl" /> : <BsEye className="text-xl" />}
                    </button>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.confirmPassword}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Resetting Password...</span>
                    </>
                  ) : (
                    <span>Reset Password</span>
                  )}
                </button>
              </form>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or</span>
              </div>
            </div>

            {/* Login Link */}
            <div className="text-center">
              <p className="text-gray-600">
                Remember your password?{" "}
                <Link 
                  to="/login" 
                  className="font-bold text-red-500 hover:text-red-600 transition-colors"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-2">
                <BsLock className="text-red-500 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Secure Reset</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-full mb-2">
                <BsLock className="text-yellow-600 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Data Protected</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-2">
                <BsLock className="text-red-500 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Encrypted</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Resetpassword;